import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  Plus, UsersThree, ShieldCheck, EnvelopeSimple, X, ArrowsClockwise,
  Trash, CheckCircle, Prohibit, Sparkle, IdentificationCard, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_CARDS = [
  {
    value: "admin",
    label: "Administrador",
    icon: ShieldCheck,
    desc: "Acesso total — financeiro, equipe, integrações e automações.",
  },
  {
    value: "operator",
    label: "Operador",
    icon: IdentificationCard,
    desc: "Conversas, agendamentos, clientes e veículos. Sem acesso a financeiro.",
  },
  {
    value: "viewer",
    label: "Consulta",
    icon: Eye,
    desc: "Somente leitura — não pode editar nada.",
  },
];

const ROLE_BADGES = {
  owner:    { label: "Proprietário", cls: "bg-slate-900 text-white" },
  admin:    { label: "Administrador", cls: "bg-slate-100 text-slate-800 border border-slate-300" },
  operator: { label: "Operador", cls: "bg-slate-100 text-slate-700 border border-slate-200" },
  viewer:   { label: "Consulta", cls: "bg-slate-50 text-slate-600 border border-slate-200" },
};

function RoleBadge({ role }) {
  const r = ROLE_BADGES[role] || ROLE_BADGES.operator;
  return (
    <span className={`inline-flex items-center text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${r.cls}`}>
      {r.label}
    </span>
  );
}

function Avatar({ name }) {
  const initials = (name || "?").split(" ").slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-semibold tracking-wider shrink-0">
      {initials}
    </div>
  );
}

export default function Team() {
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setMembers(data.members || []);
      setInvites(data.pending_invites || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/team/${m.id}`, { role });
      toast.success(`${m.name} agora é ${role === "admin" ? "Administrador" : role === "operator" ? "Operador" : "Consulta"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar a função.");
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/team/${m.id}`, { active: !m.active });
      toast.success(m.active ? `${m.name} desativado` : `${m.name} reativado`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar o status.");
    }
  };

  const remove = async (m) => {
    if (!confirm(`Remover ${m.name} da equipe? Isso é permanente.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Membro removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível remover.");
    }
  };

  const revokeInvite = async (inv) => {
    if (!confirm(`Cancelar convite enviado para ${inv.email}?`)) return;
    try {
      await api.delete(`/team/invites/${inv.id}`);
      toast.success("Convite cancelado");
      load();
    } catch {
      toast.error("Falha ao cancelar convite");
    }
  };

  const resendInvite = async (inv) => {
    try {
      await api.post(`/team/invites/${inv.id}/resend`);
      toast.success(`Convite reenviado para ${inv.email}`);
      load();
    } catch {
      toast.error("Falha ao reenviar");
    }
  };

  return (
    <>
      <Topbar
        title="Equipe"
        subtitle="Adicione e organize quem usa o AgentUS no seu estabelecimento."
        actions={
          <button
            onClick={() => setInviteOpen(true)}
            data-testid="team-invite-btn"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 text-[12px] font-medium shadow-sm"
          >
            <Plus size={13} weight="bold" /> Adicionar membro
          </button>
        }
      />

      <div className="p-6 md:p-8 space-y-6">
        {/* Explainer */}
        <section
          data-testid="team-explainer"
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 md:p-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
              <UsersThree size={20} weight="duotone" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-lg font-semibold text-slate-900">
                Sua equipe inteira, sem custo extra
              </h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Adicione quantos atendentes, gerentes ou consultores quiser — o plano é da empresa, não por usuário. Cada pessoa entra com seu próprio email e senha, e você define o que ela pode ver.
              </p>
            </div>
          </div>
        </section>

        {/* Members */}
        <section className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-700">
              {loading ? "Carregando…" : `${members.length} ${members.length === 1 ? "membro" : "membros"} ativo${members.length === 1 ? "" : "s"}`}
            </h3>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Membro</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Função</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden lg:table-cell">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => {
                  const isSelf = m.id === me?.id;
                  const isOwner = m.role === "owner";
                  return (
                    <tr key={m.id} data-testid={`team-row-${m.id}`} className={m.active ? "" : "opacity-50"}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={m.name} />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">
                              {m.name} {isSelf && <span className="text-[11px] text-slate-400 font-normal">· você</span>}
                            </div>
                            <div className="text-[12px] text-slate-500 truncate">{m.email}</div>
                            {/* Mobile: badge inline */}
                            <div className="mt-1 md:hidden flex items-center gap-1.5 flex-wrap">
                              <RoleBadge role={m.role} />
                              {!m.active && <span className="text-[10px] text-rose-600">Inativo</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {isOwner ? (
                          <RoleBadge role="owner" />
                        ) : (
                          <select
                            value={m.role}
                            onChange={(e) => changeRole(m, e.target.value)}
                            disabled={isSelf && m.role === "admin"}
                            data-testid={`team-role-${m.id}`}
                            className="text-[12.5px] rounded-lg border border-slate-200 px-2 py-1 bg-white focus:outline-none focus:border-slate-900"
                          >
                            <option value="admin">Administrador</option>
                            <option value="operator">Operador</option>
                            <option value="viewer">Consulta</option>
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {m.active ? (
                          <span className="inline-flex items-center gap-1.5 text-[12px] text-emerald-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Inativo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {!isOwner && !isSelf && (
                            <button
                              onClick={() => toggleActive(m)}
                              data-testid={`team-toggle-${m.id}`}
                              title={m.active ? "Desativar acesso" : "Reativar acesso"}
                              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
                            >
                              {m.active ? <Prohibit size={15} /> : <CheckCircle size={15} weight="fill" className="text-emerald-600" />}
                            </button>
                          )}
                          {!isOwner && !isSelf && (
                            <button
                              onClick={() => remove(m)}
                              data-testid={`team-remove-${m.id}`}
                              title="Remover da equipe"
                              className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                            >
                              <Trash size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pending invites */}
        {invites.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-slate-700 mb-2">
              {invites.length} convite{invites.length > 1 ? "s" : ""} pendente{invites.length > 1 ? "s" : ""}
            </h3>
            <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
              {invites.map((inv) => (
                <div key={inv.id} data-testid={`team-invite-${inv.id}`} className="px-4 py-3 flex items-center gap-3 flex-wrap md:flex-nowrap">
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <EnvelopeSimple size={16} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{inv.name}</div>
                    <div className="text-[12px] text-slate-500 truncate">{inv.email}</div>
                  </div>
                  <RoleBadge role={inv.role} />
                  <div className="flex items-center gap-1 ml-auto">
                    <button
                      onClick={() => resendInvite(inv)}
                      data-testid={`team-invite-resend-${inv.id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-slate-700 hover:bg-slate-100 px-2 py-1 rounded-full"
                      title="Reenviar"
                    >
                      <ArrowsClockwise size={12} weight="bold" /> Reenviar
                    </button>
                    <button
                      onClick={() => revokeInvite(inv)}
                      data-testid={`team-invite-revoke-${inv.id}`}
                      className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
                      title="Cancelar convite"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {inviteOpen && (
        <InviteModal onClose={() => setInviteOpen(false)} onCreated={() => { setInviteOpen(false); load(); }} />
      )}
    </>
  );
}

function InviteModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("operator");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({ name: false, email: false });

  // Inline validation
  const nameIssue = name.length === 0 ? null
    : name.trim().length < 2 ? "Informe o nome do membro." : null;
  const emailIssue = email.length === 0 ? null
    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? "Email inválido." : null;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true });
    if (!name.trim()) { toast.error("Informe o nome do membro."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Informe um email válido."); return; }
    setSaving(true);
    try {
      await api.post("/team/invite", { name: name.trim(), email: email.trim().toLowerCase(), role });
      toast.success(`Convite enviado para ${email}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar o convite.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-slate-950/55 backdrop-blur-md"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="team-invite-modal"
        noValidate
        className="bg-white w-full max-w-md max-h-[92vh] flex flex-col rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden"
      >
        <header className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Plus size={16} weight="bold" />
            </div>
            <div>
              <h3 className="font-heading text-[16px] font-semibold text-slate-900">Adicionar membro</h3>
              <p className="text-[11.5px] text-slate-500">Enviamos um convite por email para criar a senha.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-600 font-medium">Nome *</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              data-testid="team-invite-name"
              placeholder="Ana da Recepção"
              className={`mt-1.5 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${touched.name && nameIssue ? "border-rose-400 focus:ring-rose-500" : "border-slate-300 focus:ring-slate-900"}`}
            />
            <div className="min-h-[16px] mt-1 text-[11.5px]">
              {touched.name && nameIssue && (
                <span className="inline-flex items-center gap-1 text-rose-600">
                  <span className="w-1 h-1 rounded-full bg-rose-500" />
                  {nameIssue}
                </span>
              )}
            </div>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-600 font-medium">Email *</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              data-testid="team-invite-email"
              placeholder="ana@empresa.com"
              className={`mt-1.5 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${touched.email && emailIssue ? "border-rose-400 focus:ring-rose-500" : "border-slate-300 focus:ring-slate-900"}`}
            />
            <div className="min-h-[16px] mt-1 text-[11.5px]">
              {touched.email && emailIssue && (
                <span className="inline-flex items-center gap-1 text-rose-600">
                  <span className="w-1 h-1 rounded-full bg-rose-500" />
                  {emailIssue}
                </span>
              )}
            </div>
          </label>

          <div>
            <span className="text-[11px] uppercase tracking-wider text-slate-600 font-medium">Função *</span>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {ROLE_CARDS.map((rc) => {
                const Icon = rc.icon;
                const selected = role === rc.value;
                return (
                  <button
                    key={rc.value}
                    type="button"
                    onClick={() => setRole(rc.value)}
                    data-testid={`team-invite-role-${rc.value}`}
                    className={`text-left p-3 rounded-xl border transition-all flex items-start gap-3 ${selected ? "border-slate-900 bg-slate-50 shadow-sm" : "border-slate-200 hover:border-slate-400"}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${selected ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                      <Icon size={15} weight="fill" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 text-[13px]">{rc.label}</div>
                      <p className="text-[11.5px] text-slate-600 leading-relaxed mt-0.5">{rc.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <footer className="bg-slate-50 border-t border-slate-100 px-5 md:px-6 py-3.5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="team-invite-submit"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? <Sparkle size={13} weight="fill" className="animate-pulse" /> : <EnvelopeSimple size={13} weight="bold" />}
            {saving ? "Enviando…" : "Enviar convite"}
          </button>
        </footer>
      </form>
    </div>
  );
}
